import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useCancelInvoice, useCanCollectInvoice } from '@Hooks/Billing';
import { useInvoiceAttachments } from '@Hooks/Billing/useInvoiceAttachments';
import { useInvoicePrintPreview, usePrintOptions } from '@Hooks/Documents';
import { usePrepareEmail } from '@Hooks/Mailing';
import { type ApiInvoice, EnumProcessState, type SendPrepareInvoiceRequest } from '@Model';
import {
    EditFrameMode,
    EnumStatus,
    type IEditFrame,
    isLinked,
    isNullOrEmpty,
    useModalState,
} from '@synqo/frontend-component-library';

// @ai-generated 2026-09-18T07:55:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5

export function useInvoiceActions(frame: IEditFrame<ApiInvoice>) {
    const invoice = frame.data;
    const hasPermission = useHasPermission();

    const isInViewMode = frame.mode === EditFrameMode.View;

    const {
        isCashInvoice,
        isStateApproved,
        isCancellation,
        canCollect,
        canApprove,
        collectionActionText,
        collectionTooltip,
    } = useCanCollectInvoice(invoice);

    const approvalModal = useModalState();
    const cancellationModal = useModalState();
    const cashPaymentModal = useModalState();

    const { prepareEmail, isPreparing, emailRequest, setEmailRequest } =
        usePrepareEmail<SendPrepareInvoiceRequest>('/prepare_invoice_email');

    const printOptionsInfo = usePrintOptions(
        'invoice',
        invoice.id,
        isInViewMode && invoice.status !== EnumStatus.Draft,
    );
    const previewInvoice = useInvoicePrintPreview(printOptionsInfo);

    const attachmentsInfo = useInvoiceAttachments(invoice.id);
    const canManageAttachments = isInViewMode && hasPermission(Permission.BillingInvoicesAttachmentsManage);

    const cancelInvoice = useCancelInvoice();

    /** Approving saves first: what the user typed has to be part of what gets approved. */
    const approveInvoice = async () => {
        const onSave = frame.events.onSave;
        if (onSave) {
            const saveResult = await onSave();
            if (!saveResult) {
                approvalModal.close();
                return false;
            }
        }

        approvalModal.close();
        return frame.requestUpdateState((latest, saveData) =>
            saveData({
                ...latest,
                process_state: EnumProcessState.Approved,
            }),
        );
    };

    const confirmCancellation = (cancellationReason: unknown) => {
        if (typeof cancellationReason !== 'string' || isNullOrEmpty(cancellationReason)) {
            return;
        }
        cancellationModal.close();
        void cancelInvoice(invoice.id, cancellationReason);
    };

    return {
        isCashInvoice,
        isCancellation,
        canApprove,
        canCollect,
        collectionActionText,
        collectionTooltip,

        canDelete: !isStateApproved,
        canCancel: !isLinked(invoice.related_cancellation) && isStateApproved && !isCancellation,

        canReadAttachments: hasPermission(Permission.BillingInvoicesAttachmentsRead),
        canManageAttachments,
        canDownloadAttachments: isInViewMode,
        attachmentsInfo,

        printOptionsInfo,
        previewInvoice,
        prepareEmail,
        isPreparing,
        emailRequest,
        setEmailRequest,
        onEmailSent: async () => {
            await frame.refetchData();
        },

        approvalModal,
        cancellationModal,
        cashPaymentModal,

        approveInvoice,
        confirmCancellation,
    };
}

export type InvoiceActions = ReturnType<typeof useInvoiceActions>;
